// v2.2 — adds inline checklist rendering for tasks with checklist_items
import { useState, useEffect, useCallback } from 'react'

const safeDate = (d, fmt) => {
  if (!d || typeof d !== 'string') return null
  // For date-only strings (YYYY-MM-DD), add T12:00:00 to avoid timezone shift
  // For full timestamps, just use as-is
  const clean = /^\d{4}-\d{2}-\d{2}$/.test(d.trim()) ? d + 'T12:00:00' : d
  const date = new Date(clean)
  if (isNaN(date.getTime())) return null
  try { return format(date, fmt) } catch { return null }
}
const isBeforeToday = (d) => {
  if (!d || typeof d !== 'string') return false
  try {
    const clean = /^\d{4}-\d{2}-\d{2}$/.test(d.trim()) ? d + 'T12:00:00' : d
    return new Date(clean) < new Date()
  } catch { return false }
}
import TaskComments from './TaskComments'
import { X, ExternalLink, Edit2, CheckCircle, Clock, AlertCircle } from 'lucide-react'
import { supabase } from './supabase'
import { getServiceArea, getStatus, STATUSES, getApprovalPath } from './constants'
import { useToast } from './ToastContext'
import { format } from 'date-fns'
import { AEOGridEditor } from './AEOGridTab'
import { TaskTimerButton } from './TaskTimer'
import { AccessConfirmPanel } from './ClientAccessTab'
import { ArchiveClientModal } from './offboarding'


function getNextDueDate(task) {
  const now = new Date()
  if (task.recurrence_type === 'weekly' && task.recurrence_day_of_week !== null) {
    const next = new Date()
    const day = task.recurrence_day_of_week
    const diff = (day - now.getDay() + 7) % 7 || 7
    next.setDate(now.getDate() + diff)
    return next.toISOString().slice(0, 10)
  }
  if (task.recurrence_type === 'monthly' && task.recurrence_day_of_month) {
    const next = new Date()
    next.setMonth(next.getMonth() + 1)
    const day = task.recurrence_day_of_month === 31
      ? new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate()
      : task.recurrence_day_of_month
    next.setDate(day)
    return next.toISOString().slice(0, 10)
  }
  if (task.recurrence_type === 'custom' && task.recurrence_interval_days) {
    const next = new Date()
    next.setDate(now.getDate() + task.recurrence_interval_days)
    return next.toISOString().slice(0, 10)
  }
  return null
}

// Render checklist item text, auto-detecting URLs and rendering them as clickable links.
// Supports the pattern: "Some text https://example.com more text" → renders the URL as a link.
function ChecklistItemText({ text }) {
  if (!text) return null
  const urlRegex = /(https?:\/\/[^\s]+)/g
  const parts = text.split(urlRegex)
  return (
    <>
      {parts.map((part, i) => {
        if (urlRegex.test(part)) {
          // reset regex state since we used .test() with /g flag
          urlRegex.lastIndex = 0
          return (
            <a key={i} href={part} target="_blank" rel="noreferrer"
              onClick={e => e.stopPropagation()}
              style={{ color: 'var(--teal)', textDecoration: 'underline' }}>
              {part.length > 60 ? part.slice(0, 60) + '...' : part}
            </a>
          )
        }
        return <span key={i}>{part}</span>
      })}
    </>
  )
}

export default function TaskDetail({ task, profiles, clients, currentUser, onClose, onEdit, onStatusChange, onViewClientProfile, onTaskUpdated, onClientArchived }) {
  const toast = useToast()
  // Offboarding: the owner's checklist ends with archiving the client.
  const [showArchive, setShowArchive] = useState(false)
  const [updatingStatus, setUpdatingStatus] = useState(false)
  const [activeTab, setActiveTab] = useState('details')
  const [commentCount, setCommentCount] = useState(0)
  const [showRevisionModal, setShowRevisionModal] = useState(false)
  const [revisionNote, setRevisionNote] = useState('')
  const [submittingRevision, setSubmittingRevision] = useState(false)
  const [showFollowUpModal, setShowFollowUpModal] = useState(false)
  const [followUpText, setFollowUpText] = useState('')
  const [submittingFollowUp, setSubmittingFollowUp] = useState(false)
  const [showGridEditor, setShowGridEditor] = useState(false)
  const [latestRevisionNote, setLatestRevisionNote] = useState(null)
  const [newDocLink, setNewDocLink] = useState('')
  const [newLoomLink, setNewLoomLink] = useState('')
  const [newDeliverable, setNewDeliverable] = useState('')
  const [deliverableNote, setDeliverableNote] = useState(task?.deliverable_notes || '')
  const [savingLinks, setSavingLinks] = useState(false)
  const [localTask, setLocalTask] = useState(task)
  const [savingChecklist, setSavingChecklist] = useState(false)

  useEffect(() => {
    if (!task?.id) return
    supabase.from('task_comments').select('id', { count: 'exact' }).eq('task_id', task.id).then(({ count }) => setCommentCount(count || 0))
  }, [task?.id])

  useEffect(() => {
    if (!task?.id) return
    if (task.status === 'needs_revision') {
      supabase.from('task_comments')
        .select('content')
        .eq('task_id', task.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .then(({ data }) => {
          if (data && data[0]) {
            const raw = data[0].content || ''
            const cleaned = raw.replace('\u21a9 Revision requested: ', '').replace('Revision requested: ', '').trim()
            setLatestRevisionNote(cleaned || raw)
          }
        })
    } else {
      setLatestRevisionNote(null)
    }
  }, [task?.id, task?.status])

  // Keep localTask in sync when the task prop changes (e.g. switching between tasks)
  useEffect(() => {
    setLocalTask(task)
  }, [task?.id])

  // Guard: if no task is provided, render nothing (prevents crashes when a
  // caller opens the detail panel with a missing/var task).
  if (!task) return null


  const assignedTo = profiles.find(p => p.id === task.assigned_to)
  const assignedBy = profiles.find(p => p.id === task.assigned_by)
  const sa = getServiceArea(task.service_area)
  const st = getStatus(task.status)

  // Checklist helpers — toggle a single item by index and save the whole array
  // Items whose text starts with "## " are treated as section headers (visual only, not counted)
  const checklistItems = Array.isArray(localTask.checklist_items) ? localTask.checklist_items : []
  const isHeaderItem = (it) => typeof it?.text === 'string' && it.text.trim().startsWith('## ')
  const realChecklistItems = checklistItems.filter(it => !isHeaderItem(it))
  const checklistDone = realChecklistItems.filter(it => it?.checked).length
  const checklistTotal = realChecklistItems.length

  const toggleChecklistItem = async (idx) => {
    // Use the CURRENT localTask checklist (not the closure-captured one) so rapid clicks compose correctly
    const current = (localTask.checklist_items || [])
    const updated = current.map((it, i) => i === idx ? { ...it, checked: !it?.checked } : it)
    // Optimistic update — happens immediately, no wait
    setLocalTask(prev => ({ ...prev, checklist_items: updated }))
    setSavingChecklist(true)
    const { error } = await supabase.from('tasks').update({ checklist_items: updated }).eq('id', task.id)
    setSavingChecklist(false)
    if (error) {
      // Revert on error
      setLocalTask(prev => ({ ...prev, checklist_items: current }))
      toast('Error saving checklist: ' + error.message, 'error')
    } else {
      // Notify the parent so its cached tasks array stays in sync
      onTaskUpdated && onTaskUpdated({ ...localTask, checklist_items: updated })
    }
  }

  const updateStatus = async (newStatus, notifyUserId) => {
    setUpdatingStatus(true)
    try {
      const { error } = await supabase.from('tasks').update({ status: newStatus }).eq('id', task.id)
      if (error) throw error

      // Sync to Google Sheets
      supabase.functions.invoke('sync-tasks-to-sheets', { body: { task_id: task.id } })
      const messages = {
        ready_for_review: `"${task.title}" is ready for your review`,
        awaiting_client: `"${task.title}" has been approved and sent to client`,
        goes_live: `"${task.title}" is approved and ready to deploy`,
        live: `"${task.title}" is now live`,
        approved: `"${task.title}" has been approved`,
        blocked: `"${task.title}" has been blocked`,
      }
      if (notifyUserId && notifyUserId !== currentUser.id && messages[newStatus]) {
        await supabase.from('notifications').insert({
          user_id: notifyUserId,
          task_id: task.id,
          type: newStatus,
          message: messages[newStatus],
        })
      }

      // Sync parent blog_assignment status when a blog phase task is updated
      if (task.blog_assignment_id && task.blog_phase) {
        const phaseAdvancesOnStatuses = ['approved', 'live', 'internally_approved', 'awaiting_client', 'client_approved']
        if (phaseAdvancesOnStatuses.includes(newStatus)) {
          const nextBlogStatus = {
            draft: 'review',
            review: 'client_review',
            client_review: 'publishing',
            publish_checklist: 'complete',
          }[task.blog_phase]
          // For the review phase, internally_approved or awaiting_client means we've moved to client review
          // For client_review, client_approved or approved means we've moved to publishing
          if (nextBlogStatus) {
            await supabase.from('blog_assignments').update({ status: nextBlogStatus }).eq('id', task.blog_assignment_id)
          }
          // Also mark the draft task approved when the review phase task is approved/internally approved
          if (task.blog_phase === 'review') {
            await supabase.from('tasks').update({ status: 'approved' })
              .eq('blog_assignment_id', task.blog_assignment_id).eq('blog_phase', 'draft')
          }
        }
      }

      // Spawn next recurring task if completed
      if (['approved', 'live'].includes(newStatus) && task.is_recurring && task.recurrence_type) {
        const nextDue = getNextDueDate(task)
        if (nextDue) {
          await supabase.from('tasks').insert({
            title: task.title,
            description: task.description,
            client_id: task.client_id,
            service_area: task.service_area,
            service_subarea: task.service_subarea,
            assigned_to: task.assigned_to,
            assigned_by: task.assigned_by,
            approval_path: task.approval_path,
            is_onboarding_task: task.is_onboarding_task,
            client_visible: task.client_visible,
            is_recurring: true,
            recurrence_type: task.recurrence_type,
            recurrence_day_of_week: task.recurrence_day_of_week,
            recurrence_day_of_month: task.recurrence_day_of_month,
            recurrence_interval_days: task.recurrence_interval_days,
            recurrence_parent_id: task.id,
            date_due: nextDue,
            status: 'not_started',
          })
        }
      }

      toast('Status updated', 'success')
      onStatusChange(task.id, newStatus)
    } catch (e) {
      toast('Error: ' + e.message, 'error')
    } finally {
      setUpdatingStatus(false)
    }
  }

  const addLink = async (field, value, setter) => {
    if (!value.trim()) return
    setSavingLinks(true)
    const current = task[field] || []
    const updated = [...current, value.trim()]
    const { error } = await supabase.from('tasks').update({ [field]: updated }).eq('id', task.id)
    if (!error) {
      toast('Link added', 'success')
      setter('')
      // Update local display immediately
      setLocalTask(prev => ({ ...prev, [field]: updated }))
    } else {
      toast('Error adding link', 'error')
    }
    setSavingLinks(false)
  }

  const saveDeliverableNote = async () => {
    setSavingLinks(true)
    const { error } = await supabase.from('tasks').update({ deliverable_notes: deliverableNote }).eq('id', task.id)
    if (!error) toast('Saved', 'success')
    else toast('Error saving', 'error')
    setSavingLinks(false)
  }

  const submitRevision = async () => {
    if (!revisionNote.trim()) return
    setSubmittingRevision(true)
    try {
      // Update status
      const { error } = await supabase.from('tasks').update({ status: 'needs_revision' }).eq('id', task.id)
      if (error) throw error

      // Post revision comment
      await supabase.from('task_comments').insert({
        task_id: task.id,
        author_id: currentUser.id,
        content: `↩ Revision requested: ${revisionNote.trim()}`,
        mentions: task.assigned_to ? [task.assigned_to] : [],
      })

      // Notify assignee
      if (task.assigned_to && task.assigned_to !== currentUser.id) {
        await supabase.from('notifications').insert({
          user_id: task.assigned_to,
          task_id: task.id,
          type: 'needs_revision',
          message: `"${task.title}" needs revisions: ${revisionNote.trim().slice(0, 80)}${revisionNote.length > 80 ? '...' : ''}`,
        })
      }

      // Also notify collaborators
      for (const collabId of (task.collaborator_ids || [])) {
        if (collabId !== currentUser.id) {
          await supabase.from('notifications').insert({
            user_id: collabId,
            task_id: task.id,
            type: 'needs_revision',
            message: `"${task.title}" needs revisions`,
          })
        }
      }

      toast('Revision requested', 'success')
      setShowRevisionModal(false)
      setRevisionNote('')
      onStatusChange(task.id, 'needs_revision')
    } catch (e) {
      toast('Error: ' + e.message, 'error')
    } finally {
      setSubmittingRevision(false)
    }
  }

  // Add a follow-up step: appends a checklist item to THIS task and flips it
  // back to in_progress so the assignee sees it in their queue again.
  const submitFollowUp = async () => {
    if (!followUpText.trim()) return
    setSubmittingFollowUp(true)
    try {
      const current = Array.isArray(localTask.checklist_items) ? localTask.checklist_items : []
      const updated = [...current, { text: followUpText.trim(), checked: false }]

      const { error } = await supabase.from('tasks')
        .update({ checklist_items: updated, status: 'in_progress' })
        .eq('id', task.id)
      if (error) throw error

      // Keep local state + parent cache in sync
      setLocalTask(prev => ({ ...prev, checklist_items: updated, status: 'in_progress' }))
      onTaskUpdated && onTaskUpdated({ ...localTask, checklist_items: updated, status: 'in_progress' })

      // Sync to Google Sheets
      supabase.functions.invoke('sync-tasks-to-sheets', { body: { task_id: task.id } })

      // Notify the assignee that there's a new step + the task is active again
      if (task.assigned_to && task.assigned_to !== currentUser.id) {
        await supabase.from('notifications').insert({
          user_id: task.assigned_to,
          task_id: task.id,
          type: 'comment',
          message: `Follow-up step added to "${task.title}": ${followUpText.trim().slice(0, 80)}${followUpText.length > 80 ? '...' : ''}`,
        })
      }

      toast('Follow-up step added — task is back in progress', 'success')
      setShowFollowUpModal(false)
      setFollowUpText('')
      onStatusChange(task.id, 'in_progress')
    } catch (e) {
      toast('Error: ' + e.message, 'error')
    } finally {
      setSubmittingFollowUp(false)
    }
  }

  const isAssignee = currentUser.id === task.assigned_to || (task.collaborator_ids || []).includes(currentUser.id)
  const isAssigner = currentUser.id === task.assigned_by
  const canApprove = isAssigner && task.status === 'ready_for_review'

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 90,
      display: 'flex', justifyContent: 'flex-end'
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: 'rgba(30,42,58,0.3)', position: 'absolute', inset: 0
      }} onClick={onClose} />
      <div style={{
        position: 'relative', width: 480, background: '#fff',
        boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column',
        height: '100%', overflowY: 'auto', animation: 'slideInRight 0.2s ease'
      }}>
        <style>{`@keyframes slideInRight { from { transform: translateX(40px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>

        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, paddingRight: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              {sa && <span className="badge" style={{ background: sa.color + '18', color: sa.color }}>
                <span className="service-dot" style={{ background: sa.color }} />{sa.label}
              </span>}
              {task.service_subarea && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{task.service_subarea}</span>}
              {task.week_number && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Week {task.week_number}</span>}
              {clients && task.client_id && (() => {
                const client = clients.find(c => c.id === task.client_id)
                return client ? (
                  <button onClick={() => onViewClientProfile && onViewClientProfile(client)}
                    style={{ background: 'var(--navy-mid)', color: '#fff', border: 'none', borderRadius: 50, padding: '2px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                    {client.name} →
                  </button>
                ) : null
              })()}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, color: 'var(--navy)', lineHeight: 1.3 }}>{task.title}</h2>
              {task.is_urgent && <span style={{ background: 'var(--red)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 50, letterSpacing: '0.5px' }}>🔴 URGENT</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn-icon" onClick={() => onEdit(task)}><Edit2 size={14} /></button>
            <button className="btn-icon" onClick={onClose}><X size={14} /></button>
          </div>
        </div>

        {/* Status + approval action bar */}
        <div style={{ padding: '12px 24px', background: st?.bg, borderBottom: '1px solid var(--border-light)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span className="status-pill" style={{ background: 'transparent', color: st?.color, fontWeight: 600 }}>
              {st?.label}
            </span>
            {task.approval_path && (
              <span style={{ fontSize: 11, color: 'var(--text-muted)', background: 'rgba(0,0,0,0.05)', padding: '2px 8px', borderRadius: 10 }}>
                {getApprovalPath(task.approval_path)?.label || task.approval_path}
              </span>
            )}
          </div>
          <div style={{ marginBottom: 8 }}>
            <TaskTimerButton task={task} />
          </div>
          {task.status === 'needs_revision' && latestRevisionNote && (
            <div style={{ background: '#fff3cd', border: '1px solid #f0d98a', borderRadius: 8, padding: '8px 12px', marginBottom: 8, fontSize: 13 }}>
              <span style={{ fontWeight: 600, color: '#8a6000' }}>↩ Revision needed: </span>
              <span style={{ color: '#5a4000' }}>{latestRevisionNote}</span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {/* Assignee actions */}
            {isAssignee && task.status === 'not_started' && (
              <button className="btn btn-sm" style={{ background: 'var(--teal)', color: '#fff' }} onClick={() => updateStatus('in_progress', null)} disabled={updatingStatus}>
                Start Task
              </button>
            )}
            {isAssignee && task.status === 'in_progress' && (
              <button className="btn btn-sm btn-gold" onClick={() => updateStatus('ready_for_review', task.assigned_by)} disabled={updatingStatus}>
                ✓ Mark Ready for Review
              </button>
            )}

            {/* Assigner/AD actions — internal approval */}
            {isAssigner && task.status === 'ready_for_review' && task.approval_path === 'none' && (
              <button className="btn btn-sm" style={{ background: 'var(--green)', color: '#fff' }} onClick={() => updateStatus('live', null)} disabled={updatingStatus}>
                ✓ Mark Done
              </button>
            )}
            {isAssigner && task.status === 'ready_for_review' && task.approval_path === 'internal' && (
              <button className="btn btn-sm" style={{ background: 'var(--green)', color: '#fff' }} onClick={() => updateStatus('approved', null)} disabled={updatingStatus}>
                ✓ Approve & Close
              </button>
            )}
            {isAssigner && task.status === 'ready_for_review' && task.approval_path === 'internal_then_live' && (
              <button className="btn btn-sm" style={{ background: 'var(--coral)', color: '#fff' }} onClick={() => updateStatus('goes_live', null)} disabled={updatingStatus}>
                ✓ Approve → Ready to Deploy
              </button>
            )}
            {isAssigner && task.status === 'ready_for_review' && task.approval_path === 'internal_client_live' && (
              <button className="btn btn-sm" style={{ background: 'var(--plum)', color: '#fff' }} onClick={() => updateStatus('internally_approved', null)} disabled={updatingStatus}>
                ✓ Internally Approved
              </button>
            )}
            {isAssigner && task.status === 'ready_for_review' && task.approval_path === 'internal_then_client_fyi' && (
              <button className="btn btn-sm" style={{ background: 'var(--plum)', color: '#fff' }} onClick={() => updateStatus('internally_approved', null)} disabled={updatingStatus}>
                ✓ Internally Approved
              </button>
            )}

            {/* Request revisions — available on all ready_for_review paths */}
            {isAssigner && task.status === 'ready_for_review' && (
              <button className="btn btn-sm" style={{ background: '#fff8e1', color: '#8a6000', border: '1.5px solid #f0d98a' }}
                onClick={() => setShowRevisionModal(true)}>
                ↩ Request Revisions
              </button>
            )}

            {/* Add a follow-up step — appends a step + reactivates the task */}
            {isAssigner && task.status === 'ready_for_review' && (
              <button className="btn btn-sm" style={{ background: '#eef4ff', color: '#2a4a84', border: '1.5px solid #b8cdf0' }}
                onClick={() => setShowFollowUpModal(true)}>
                ➕ Add Follow-Up Step
              </button>
            )}

            {/* Send to client step */}
            {isAssigner && task.status === 'internally_approved' && ['internal_client_live', 'internal_then_client_fyi'].includes(task.approval_path) && (() => {
              const docLink = [...(task.deliverable_links || []), ...(task.google_doc_links || [])][0] || ''
              const myName = profiles?.find(p => p.id === currentUser.id)?.full_name || 'LMR Team'
              const subject = encodeURIComponent(task.title + ' — Ready for Your Review')
              const body = encodeURIComponent(
                'Hi,\n\nWe\'ve completed "' + task.title + '" and it\'s ready for your review!' +
                (docLink ? '\n\n📄 View here: ' + docLink : '') +
                '\n\nPlease let us know:\n• ✅ Approved as-is\n• ✏️ Any revisions needed\n\nBest,\n' + myName
              )
              return (
                <>
                  <button className="btn btn-sm" style={{ background: '#e8f5fa', color: '#2a6e84', border: 'none', display: 'flex', alignItems: 'center', gap: 5 }}
                    onClick={() => window.open('https://mail.google.com/mail/?view=cm&su=' + subject + '&body=' + body, '_blank')}>
                    📨 Open Gmail to Send
                  </button>
                  <button className="btn btn-sm" style={{ background: 'var(--plum)', color: '#fff' }} onClick={() => updateStatus('awaiting_client', null)} disabled={updatingStatus}>
                    ✓ Mark Sent to Client
                  </button>
                </>
              )
            })()}

            {/* Post-client approval */}
            {isAssigner && task.status === 'awaiting_client' && (
              <>
                <button className="btn btn-sm" style={{ background: '#e8f5fa', color: '#2a6e84', border: 'none', display: 'flex', alignItems: 'center', gap: 5 }}
                  onClick={() => {
                    const docLink = [...(task.deliverable_links || []), ...(task.google_doc_links || [])][0] || ''
                    const myName = profiles?.find(p => p.id === currentUser.id)?.full_name || 'LMR Team'
                    const subject = encodeURIComponent(task.title + ' — Ready for Your Review')
                    const body = encodeURIComponent('Hi,\n\nWe\'ve completed "' + task.title + '" and it\'s ready for your review!' + (docLink ? '\n\n📄 View here: ' + docLink : '') + '\n\nPlease let us know:\n• ✅ Approved as-is\n• ✏️ Any revisions needed\n\nBest,\n' + myName)
                    window.open('https://mail.google.com/mail/?view=cm&su=' + subject + '&body=' + body, '_blank')
                  }}>
                  📨 Resend to Client
                </button>
                <button className="btn btn-sm" style={{ background: 'var(--green)', color: '#fff' }} onClick={() => updateStatus('client_approved', null)} disabled={updatingStatus}>
                  ✓ Client Approved
                </button>
              </>
            )}
            {isAssigner && task.status === 'client_approved' && (
              <button className="btn btn-sm" style={{ background: 'var(--coral)', color: '#fff' }} onClick={() => updateStatus('live', null)} disabled={updatingStatus}>
                🚀 Mark Live
              </button>
            )}
            {isAssigner && task.status === 'goes_live' && (
              <button className="btn btn-sm" style={{ background: 'var(--coral)', color: '#fff' }} onClick={() => updateStatus('live', null)} disabled={updatingStatus}>
                🚀 Mark Live
              </button>
            )}

            {/* Block */}
            {!['live', 'approved', 'blocked'].includes(task.status) && (
              <button className="btn btn-sm btn-ghost" style={{ color: 'var(--red)', marginLeft: 'auto' }} onClick={() => updateStatus('blocked', null)} disabled={updatingStatus}>
                ⚠ Block
              </button>
            )}
            {task.status === 'blocked' && (
              <button className="btn btn-sm btn-ghost" onClick={() => updateStatus('in_progress', null)} disabled={updatingStatus}>
                Unblock
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border-light)', padding: '0 24px' }}>
          <button onClick={() => setActiveTab('details')}
              style={{ padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: activeTab === 'details' ? 600 : 400, color: activeTab === 'details' ? 'var(--navy)' : 'var(--text-muted)', borderBottom: activeTab === 'details' ? '2px solid var(--navy-mid)' : '2px solid transparent', marginBottom: -1 }}>
              📋 Details
            </button>
            <button onClick={() => setActiveTab('comments')}
              style={{ padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: activeTab === 'comments' ? 600 : 400, color: activeTab === 'comments' ? 'var(--navy)' : 'var(--text-muted)', borderBottom: activeTab === 'comments' ? '2px solid var(--navy-mid)' : '2px solid transparent', marginBottom: -1, display: 'flex', alignItems: 'center', gap: 6 }}>
              💬 Comments {commentCount > 0 && <span style={{ background: commentCount > 0 ? 'var(--mauve)' : 'var(--text-muted)', color: '#fff', borderRadius: 50, padding: '1px 6px', fontSize: 11 }}>{commentCount}</span>}
            </button>
        </div>

        {/* Comments tab */}
        {activeTab === 'comments' && (
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <TaskComments
              taskId={task.id}
              taskTitle={task.title}
              profiles={profiles}
              currentUser={currentUser}
              assignedBy={task.assigned_by}
              assignedTo={task.assigned_to}
            />
          </div>
        )}

        {/* Details tab */}
        {activeTab === 'details' && <div style={{ padding: '20px 24px', flex: 1, overflowY: 'auto' }}>
          {/* Meta */}
          {/* Approval path — prominent at top */}
          {task.approval_path && (
            <div style={{ marginBottom: 14, padding: '10px 14px', background: 'var(--lightpurple)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Approval Path:</span>
              <span style={{ fontSize: 13, color: 'var(--navy)', fontWeight: 500 }}>{getApprovalPath(task.approval_path)?.label || task.approval_path}</span>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px', marginBottom: 20, padding: '14px 16px', background: 'var(--cream)', borderRadius: 'var(--radius-sm)' }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>Assigned To</div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{assignedTo?.full_name || '—'}</div>
              {assignedTo?.role && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{assignedTo.role.replace('_', ' ')}</div>}
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>Assigned By</div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{assignedBy?.full_name || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>Date Assigned</div>
              <div style={{ fontSize: 13 }}>{task.date_assigned ? safeDate(task.date_assigned, 'MMM d, yyyy') || '—' : '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>Due Date</div>
              <div style={{ fontSize: 13, fontWeight: task.date_due ? 500 : 400, color: task.date_due && isBeforeToday(task.date_due) && task.status !== 'approved' ? 'var(--urgent)' : 'inherit' }}>
                {task.date_due ? safeDate(task.date_due, 'MMM d, yyyy') || '—' : '—'}
              </div>
            </div>
          </div>

          {task.description && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Description</div>
              <p style={{ fontSize: 13.5, lineHeight: 1.6, color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>{task.description}</p>
            </div>
          )}

          {/* ── Onboarding access tasks: confirm right here ── */}
          {task.triggered_by?.access_item_id && (
            <AccessConfirmPanel task={task} onDone={() => updateStatus('approved')} />
          )}

          {/* ── Checklist — only shown if task has checklist_items ── */}
          {checklistTotal > 0 && (
            <div style={{ marginBottom: 20, padding: '14px 16px', background: 'var(--cream)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-light)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  📋 Checklist
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: checklistDone === checklistTotal ? '#2e6e3a' : 'var(--text-muted)' }}>
                  {checklistDone} / {checklistTotal} done
                </div>
              </div>
              {/* Progress bar */}
              <div style={{ height: 4, background: '#fff', borderRadius: 2, overflow: 'hidden', marginBottom: 12 }}>
                <div style={{
                  height: '100%',
                  width: `${(checklistDone / checklistTotal) * 100}%`,
                  background: checklistDone === checklistTotal ? '#2e6e3a' : 'var(--gold)',
                  transition: 'width 200ms',
                }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {checklistItems.map((item, idx) => {
                  const text = item?.text || ''

                  // Section header: lines starting with "## " render as a sub-heading
                  if (isHeaderItem(item)) {
                    const headerText = text.replace(/^##\s+/, '')
                    // Count how many items follow until the next header (or end)
                    let count = 0, doneInSection = 0
                    for (let j = idx + 1; j < checklistItems.length; j++) {
                      if (isHeaderItem(checklistItems[j])) break
                      count++
                      if (checklistItems[j]?.checked) doneInSection++
                    }
                    const sectionComplete = count > 0 && doneInSection === count
                    return (
                      <div key={idx} style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: sectionComplete ? '#2e6e3a' : 'var(--navy)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.6px',
                        padding: '12px 14px 10px 14px',
                        background: sectionComplete ? '#e8f5eb' : 'var(--cream)',
                        border: `1px solid ${sectionComplete ? '#c2dec8' : 'var(--border-light)'}`,
                        borderRadius: 6,
                        marginTop: idx > 0 ? 14 : 0,
                        marginBottom: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}>
                        <span>{sectionComplete && '✓ '}{headerText}</span>
                        {count > 0 && (
                          <span style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: sectionComplete ? '#2e6e3a' : 'var(--text-muted)',
                            textTransform: 'none',
                            letterSpacing: 'normal',
                          }}>{doneInSection} / {count}</span>
                        )}
                      </div>
                    )
                  }

                  const isChecked = !!item?.checked
                  const isAeoGridStep = /^Make an AEO Content Grid/i.test(text.trim())
                  const taskClient = clients?.find(c => c.id === task.client_id)
                  return (
                    <div key={idx}>
                      <label
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: 10,
                          padding: '8px 10px 8px 18px',   // slight indent so items sit under their section
                          background: isChecked ? '#e8f5eb' : '#fff',
                          border: `1px solid ${isChecked ? '#c2dec8' : 'var(--border-light)'}`,
                          borderRadius: 8,
                          cursor: 'pointer',
                          fontSize: 13,
                        }}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleChecklistItem(idx)}
                          style={{ marginTop: 2, cursor: 'pointer', flexShrink: 0 }}
                        />
                        <div style={{
                          flex: 1,
                          color: isChecked ? '#2e6e3a' : 'var(--text-primary)',
                          textDecoration: isChecked ? 'line-through' : 'none',
                          lineHeight: 1.45,
                          wordBreak: 'break-word',
                        }}>
                          <ChecklistItemText text={text} />
                        </div>
                      </label>
                      {isAeoGridStep && taskClient && (
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ marginTop: 6, marginLeft: 18 }}
                          onClick={() => setShowGridEditor(true)}>
                          ➕ Create AEO grid for {taskClient.name}
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
              {task.offboarding === 'admin' && !['approved', 'live'].includes(localTask.status) && (
                <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border-light)' }}>
                  <button className="btn btn-sm" style={{ background: '#c4962a', color: '#fff', border: 'none' }}
                    onClick={() => setShowArchive(true)}>
                    Finish and archive
                  </button>
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
                    {checklistDone < checklistTotal
                      ? `${checklistTotal - checklistDone} of your items aren't ticked yet. `
                      : ''}
                    Archives the client and ends commissions. Checks the Account Director's checklist first.
                  </div>
                </div>
              )}
            </div>
          )}

          {showArchive && (
            <ArchiveClientModal
              clientId={task.client_id}
              defaultTermDate={task.date_due || undefined}
              onClose={() => setShowArchive(false)}
              onArchived={() => { setShowArchive(false); onClientArchived ? onClientArchived() : onClose() }}
            />
          )}

          {task.notes && (
            <div style={{ marginBottom: 20, padding: '12px 14px', background: '#fdf6e3', borderRadius: 'var(--radius-sm)', border: '1px solid #f0d98a' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Internal Notes</div>
              <p style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{task.notes}</p>
            </div>
          )}

          {/* ── Deliverables — always visible ── */}
          <div style={{ marginBottom: 16, padding: '14px 16px', background: 'var(--lightgreen)', borderRadius: 'var(--radius-sm)', border: '1px solid #a8d8b8' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#2e6e3a', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>✅ Finished Work / Deliverables</div>
            {localTask.deliverable_links?.map((link, i) => (
              <a key={i} href={link} target="_blank" rel="noreferrer" className="link-tag" style={{ background: '#e8f5eb', color: '#2e6e3a', marginBottom: 6, display: 'inline-flex' }}>
                <ExternalLink size={11} /><span>{link.slice(0, 50)}</span>
              </a>
            ))}
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <input value={newDeliverable} onChange={e => setNewDeliverable(e.target.value)}
                placeholder="Paste Canva, Google Doc, or link..." onKeyDown={e => e.key === 'Enter' && addLink('deliverable_links', newDeliverable, setNewDeliverable)}
                style={{ flex: 1, padding: '6px 10px', border: '1px solid #a8d8b8', borderRadius: 8, fontSize: 12, fontFamily: 'var(--font-body)', outline: 'none', background: '#fff' }} />
              <button onClick={() => addLink('deliverable_links', newDeliverable, setNewDeliverable)} disabled={savingLinks}
                style={{ padding: '6px 12px', background: '#2e6e3a', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>Add</button>
            </div>
            <textarea value={deliverableNote} onChange={e => setDeliverableNote(e.target.value)} onBlur={saveDeliverableNote}
              placeholder="Notes about finished work..." rows={2}
              style={{ width: '100%', marginTop: 8, padding: '6px 10px', border: '1px solid #a8d8b8', borderRadius: 8, fontSize: 12, fontFamily: 'var(--font-body)', resize: 'none', outline: 'none', background: '#fff' }} />
          </div>

          {/* ── Google Docs — always visible ── */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Google Docs</div>
            <div className="link-tags" style={{ marginBottom: 6 }}>
              {(localTask.google_doc_links || []).map((link, i) => (
                <a key={i} href={link} target="_blank" rel="noreferrer" className="link-tag">
                  <ExternalLink size={11} />
                  <span>{link.slice(0, 45)}</span>
                </a>
              ))}
              {(!localTask.google_doc_links || localTask.google_doc_links.length === 0) && (
                <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No docs linked yet</span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input value={newDocLink} onChange={e => setNewDocLink(e.target.value)}
                placeholder="Paste Google Doc URL..." onKeyDown={e => e.key === 'Enter' && addLink('google_doc_links', newDocLink, setNewDocLink)}
                style={{ flex: 1, padding: '6px 10px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 12, fontFamily: 'var(--font-body)', outline: 'none' }} />
              <button onClick={() => addLink('google_doc_links', newDocLink, setNewDocLink)} disabled={savingLinks}
                style={{ padding: '6px 12px', background: 'var(--lightblue)', color: 'var(--navy)', border: 'none', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>Add</button>
            </div>
          </div>

          {/* ── Loom Videos — always visible ── */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Loom Videos</div>
            <div className="link-tags" style={{ marginBottom: 6 }}>
              {(localTask.loom_links || []).map((link, i) => (
                <a key={i} href={link} target="_blank" rel="noreferrer" className="link-tag loom">
                  <ExternalLink size={11} />
                  <span>{link.slice(0, 45)}</span>
                </a>
              ))}
              {(!localTask.loom_links || localTask.loom_links.length === 0) && (
                <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No videos linked yet</span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input value={newLoomLink} onChange={e => setNewLoomLink(e.target.value)}
                placeholder="Paste Loom URL..." onKeyDown={e => e.key === 'Enter' && addLink('loom_links', newLoomLink, setNewLoomLink)}
                style={{ flex: 1, padding: '6px 10px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 12, fontFamily: 'var(--font-body)', outline: 'none' }} />
              <button onClick={() => addLink('loom_links', newLoomLink, setNewLoomLink)} disabled={savingLinks}
                style={{ padding: '6px 12px', background: 'var(--lightpurple)', color: 'var(--navy)', border: 'none', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>Add</button>
            </div>
          </div>

          {/* Collaborators */}
          {task.collaborator_ids?.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Collaborators</div>
              {task.status === 'needs_revision' && latestRevisionNote && (
            <div style={{ background: '#fff3cd', border: '1px solid #f0d98a', borderRadius: 8, padding: '8px 12px', marginBottom: 8, fontSize: 13 }}>
              <span style={{ fontWeight: 600, color: '#8a6000' }}>↩ Revision needed: </span>
              <span style={{ color: '#5a4000' }}>{latestRevisionNote}</span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {task.collaborator_ids.map(id => {
                  const p = profiles.find(p => p.id === id)
                  return p ? <span key={id} className="badge" style={{ background: 'var(--lightpurple)', color: 'var(--navy)' }}>{p.full_name}</span> : null
                })}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
            {task.is_onboarding_task && <span className="badge" style={{ background: '#fdf6e3', color: 'var(--gold)' }}>Onboarding</span>}
            {!task.client_visible && <span className="badge" style={{ background: '#fdf0f0', color: 'var(--red)' }}>Hidden from Client</span>}
          </div>
        </div>}
      </div>

      {/* Revision Request Modal */}
      {showRevisionModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(26,43,74,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: 28, width: '100%', maxWidth: 460, boxShadow: 'var(--shadow-lg)' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, color: 'var(--navy)', marginBottom: 6 }}>Request Revisions</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>Explain what needs to be fixed. This will be posted as a comment and the assignee will be notified.</div>
            <textarea
              value={revisionNote}
              onChange={e => setRevisionNote(e.target.value)}
              placeholder="What needs to be revised? Be specific..."
              autoFocus
              style={{ width: '100%', minHeight: 100, padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 10, fontFamily: 'var(--font-body)', fontSize: 13.5, resize: 'vertical', outline: 'none', marginBottom: 16 }}
              onFocus={e => e.target.style.borderColor = 'var(--mauve)'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => { setShowRevisionModal(false); setRevisionNote('') }}>Cancel</button>
              <button className="btn btn-sm" style={{ background: '#c4962a', color: '#fff' }}
                onClick={submitRevision}
                disabled={submittingRevision || !revisionNote.trim()}>
                {submittingRevision ? 'Sending...' : '↩ Send Back for Revisions'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Follow-Up Step Modal */}
      {showFollowUpModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(26,43,74,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: 28, width: '100%', maxWidth: 460, boxShadow: 'var(--shadow-lg)' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, color: 'var(--navy)', marginBottom: 6 }}>Add Follow-Up Step</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
              Adds a new step to this task's checklist and puts the task back <strong>in progress</strong> so {assignedTo?.full_name || 'the assignee'} sees it again. The original work stays intact.
            </div>
            <textarea
              value={followUpText}
              onChange={e => setFollowUpText(e.target.value)}
              placeholder="What's the follow-up step? Be specific..."
              autoFocus
              style={{ width: '100%', minHeight: 90, padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 10, fontFamily: 'var(--font-body)', fontSize: 13.5, resize: 'vertical', outline: 'none', marginBottom: 16 }}
              onFocus={e => e.target.style.borderColor = 'var(--mauve)'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => { setShowFollowUpModal(false); setFollowUpText('') }}>Cancel</button>
              <button className="btn btn-sm" style={{ background: '#2a4a84', color: '#fff' }}
                onClick={submitFollowUp}
                disabled={submittingFollowUp || !followUpText.trim()}>
                {submittingFollowUp ? 'Adding...' : '➕ Add Step & Reactivate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AEO Grid editor — opened from the "Make an AEO Content Grid" checklist step */}
      {showGridEditor && (() => {
        const taskClient = clients?.find(c => c.id === task.client_id)
        if (!taskClient) return null
        return (
          <AEOGridEditor
            client={taskClient}
            profiles={profiles}
            currentUser={currentUser}
            grid={null}
            onClose={() => setShowGridEditor(false)}
            onSaved={() => { setShowGridEditor(false); toast('AEO grid saved to this client\u2019s AEO Grids', 'success') }}
          />
        )
      })()}
    </div>
  )
}